// app/search/SearchResultsClient.tsx
"use client";

import { useEffect, useLayoutEffect, useState, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useMobile } from "@/lib/use-mobile";
import { Building2, User, AlertCircle, Loader2, ExternalLink, ArrowLeft, ArrowDown, ArrowUp, ArrowUpDown, Copy, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSearchMode, useSearchActive, useAdvancedFilters } from "@/lib/search-mode-atom";
import SearchPill from "@/components/SearchPill";
import { DatadiniIcon } from "@/app/components/Icons";
import { CLOSED_STATUSES } from "@/lib/status-groups";

interface Company {
  company_number: string;
  company_name: string;
  company_status?: string;
  company_type?: string;
  incorporation_date?: string;
  registered_address_postcode?: string;
  registered_address_town?: string;
  full_address?: string;
  sector?: string;
}

// New schema: one document per role
interface PersonRole {
  id: string;
  company_number: string;
  company_name: string;
  person_name: string;
  nationality: string | null;
  dob_month: number | null;
  dob_year: number | null;
  appointment_type: string;
  role_type: "OFFICER" | "PSC";
  is_active: boolean;
  appointed_on: string | null;
  resigned_on: string | null;
}

// Grouped view: one card per unique person
interface GroupedPerson {
  person_name: string;
  dob_year: number | null;
  dob_month: number | null;
  nationality: string | null;
  roles: PersonRole[];
}

interface SearchResults {
  companies: Company[];
  people: PersonRole[];
  total_companies: number;
  total_people: number;
}

interface SearchResultsClientProps {
  query: string;
  initialMode?: boolean;
  showDebug?: boolean;
  isSubscribed?: boolean;
}

type AdvancedSortKey =
  | "Company_Name"
  | "Company_Number"
  | "Company_Status"
  | "Industry"
  | "Division"
  | "Post_Town"
  | "County"
  | "Incorporation_Date"
  | "Age_Band"
  | "Turnover_Year1"
  | "Profit_After_Tax_Year1"
  | "Total_Assets_Year1"
  | "Current_Liabilities_Year1"
  | "Cash_Year1"
  | "Total_Debt_Year1"
  | "Employee_Band"
  | "Board_Structure"
  | "Director_Avg_Age";

interface AdvancedSearchResult {
  Company_Number: string;
  Company_Name: string;
  Company_Status: string | null;
  Industry?: string | null;
  Division?: string | null;
  Board_Structure?: string | null;
  Director_Avg_Age?: number | null;
  Year1_FY?: number | null;
  Currency?: string | null;
  Total_Assets_Year1?: number | null;
  Current_Liabilities_Year1?: number | null;
  Turnover_Year1?: number | null;
  Turnover_Band?: string | null;
  Profit_After_Tax_Year1?: number | null;
  Cash_Year1?: number | null;
  Total_Debt_Year1?: number | null;
  Employee_Band?: string | null;
  Incorporation_Date?: string | null;
  Age_Band?: string | null;
  County?: string | null;
  Postcode?: string | null;
  Post_Town?: string | null;
  Full_Address?: string | null;
  Companies_House_Link?: string | null;
  Is_Open?: boolean | number | null;
  // Contact fields
  Website?: string | null;
  Domain?: string | null;
  Email?: string | null;
  Phone?: string | null;
  Twitter?: string | null;
  LinkedIn?: string | null;
  Facebook?: string | null;
  Instagram?: string | null;
  YouTube?: string | null;
}


// Helper to generate URL slug from company name
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50);
}

/**
 * Build a person profile slug from available data.
 * Format: {name}-{year}-{month}-{nationality}  (all segments optional)
 * Invalid/out-of-range values are silently dropped.
 */
function buildPersonSlug(
  personName: string,
  dobYear: number | null,
  dobMonth: number | null,
  nationality: string | null
): string {
  const nameSlug = (personName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .substring(0, 60);
  // Sanity-check year: must be a plausible birth year
  const validYear = dobYear && dobYear >= 1900 && dobYear <= new Date().getFullYear() - 16
    ? dobYear : null;
  // Sanity-check month: must be 1–12
  const validMonth = dobMonth && dobMonth >= 1 && dobMonth <= 12
    ? dobMonth : null;
  if (!validYear) return nameSlug;
  let slug = `${nameSlug}-${validYear}`;
  if (validMonth) slug += `-${String(validMonth).padStart(2, "0")}`;
  if (nationality) slug += `-${slugify(nationality)}`;
  return slug;
}

// Generate company URL with slug for SEO
function getCompanyUrl(company: Company, advancedMode: boolean): string {
  const nameSlug = slugify(company.company_name);
  const base = `/company/${company.company_number}-${nameSlug}`;
  return advancedMode ? `${base}?mode=advanced` : base;
}

export default function SearchResultsClient({
  query,
  initialMode = false,
  showDebug = false,
  isSubscribed = false,
}: SearchResultsClientProps) {
  const [results, setResults] = useState<SearchResults | null>(null);

  // Group the flat role-per-hit list into one entry per unique person
  const groupedPeople = useRef<GroupedPerson[]>([]);
  const prevPeopleHits = useRef<PersonRole[] | null>(null);
  if (results?.people && results.people !== prevPeopleHits.current) {
    prevPeopleHits.current = results.people;
    const map = new Map<string, GroupedPerson>();
    for (const role of results.people) {
      const validYear = role.dob_year && role.dob_year >= 1900 && role.dob_year <= new Date().getFullYear() - 16 ? role.dob_year : null;
      const validMonth = role.dob_month && role.dob_month >= 1 && role.dob_month <= 12 ? role.dob_month : null;
      const key = `${role.person_name}__${validYear ?? ""}__${validMonth ?? ""}__${(role.nationality ?? "").toUpperCase()}`;
      if (!map.has(key)) {
        map.set(key, {
          person_name: role.person_name,
          dob_year: role.dob_year,
          dob_month: role.dob_month,
          nationality: role.nationality,
          roles: [],
        });
      }
      map.get(key)!.roles.push(role);
    }
    groupedPeople.current = Array.from(map.values());
  }

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageReady, setPageReady] = useState(false);
  const [advancedResults, setAdvancedResults] = useState<AdvancedSearchResult[]>([]);
  const [advancedTotal, setAdvancedTotal] = useState(0);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedError, setAdvancedError] = useState<string | null>(null);
  const [advancedLoadingVisible, setAdvancedLoadingVisible] = useState(false);
  const [advancedStageIndex, setAdvancedStageIndex] = useState(0);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const [exportStatus, setExportStatus] = useState<"idle" | "done" | "error">("idle");
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [advancedDebug, setAdvancedDebug] = useState<{
    payload: Record<string, unknown>;
    responseStatus: number | null;
    responseBody: unknown;
    durationMs: number | null;
    queryTimeMs: number | null;
  } | null>(null);
  const [advancedPage, setAdvancedPage] = useState(1);
  const [advancedPageSize, setAdvancedPageSize] = useState(50);
  const [advancedSort, setAdvancedSort] = useState<{
    key: AdvancedSortKey;
    direction: "asc" | "desc";
  }>({ key: "Company_Name", direction: "asc" });
  const [mounted, setMounted] = useState(false);
  const { filters: advancedFilters } = useAdvancedFilters();
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [bottomScrollWidth, setBottomScrollWidth] = useState(0);
  const advancedLoadingStartRef = useRef<number | null>(null);
  const advancedLoadingTimeoutRef = useRef<number | null>(null);
  const advancedStageTimeoutsRef = useRef<number[]>([]);
  const clampStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  } as const;
  const buildAdvancedPayload = (overrides?: { limit?: number; offset?: number }) => {
    const payload: Record<string, unknown> = {
      reliable_only: true,
      limit: overrides?.limit ?? advancedPageSize,
      offset: overrides?.offset ?? (advancedPage - 1) * advancedPageSize,
    };
    if (advancedFilters.statusGroup) {
      payload.company_status_group = [advancedFilters.statusGroup];
    }
    if (query.trim()) {
      payload.company_name = query;
    }
    if (advancedFilters.sector?.length) {
      const sectors = advancedFilters.sector.map((value) => value.trim()).filter(Boolean);
      if (sectors.length > 0) {
        payload.sector = sectors;
      }
    }
    if (advancedFilters.county?.length) {
      payload.county = advancedFilters.county.map((value) => value.trim()).filter(Boolean);
    }
    if (advancedFilters.postcode?.trim()) {
      payload.postcode = [advancedFilters.postcode.trim()];
    }
    if (advancedFilters.division?.length) {
      payload.division = advancedFilters.division.map((value) => value.trim()).filter(Boolean);
    }
    if (advancedFilters.turnoverBand?.length) {
      payload.turnover_band = advancedFilters.turnoverBand.map((value) => value.trim()).filter(Boolean);
    }
    if (advancedFilters.employeeBand?.length) {
      payload.employee_band = advancedFilters.employeeBand.map((value) => value.trim()).filter(Boolean);
    }
    if (advancedFilters.boardStructure?.length) {
      payload.board_structure = advancedFilters.boardStructure;
    }
    if (advancedFilters.ageBand?.length) {
      payload.age_band = advancedFilters.ageBand.map((value) => value.trim()).filter(Boolean);
    }
    if (advancedFilters.incorporatedToday) {
      payload.incorporated_today = true;
    }
    if (advancedFilters.hasContact !== null && advancedFilters.hasContact !== undefined) {
      payload.has_contact = advancedFilters.hasContact;
    }
    payload.sort_by = advancedSort.key;
    payload.sort_order = advancedSort.direction.toUpperCase();
    return payload;
  };
  const runAdvancedSearch = async (payload: Record<string, unknown>, signal?: AbortSignal) => {
    const response = await fetch("/api/advanced-search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    });

    const rawText = await response.text();
    let body: unknown = null;
    if (rawText) {
      try {
        body = JSON.parse(rawText);
      } catch {
        body = rawText;
      }
    }

    if (!response.ok) {
      const message =
        typeof body === "string" ? body : JSON.stringify(body ?? "Advanced search failed");
      throw new Error(message);
    }

    return { body, status: response.status };
  };
  
  // Use global search mode and pill visibility
  const { isAdvanced: advancedMode, setIsAdvanced: setAdvancedMode } = useSearchMode();
  const { searchActive, setSearchActive } = useSearchActive();
  const router = useRouter();

  // Prevent body scroll while this fixed layout is active
  const handleAdvancedSort = (key: AdvancedSortKey) => {
    setAdvancedPage(1);
    setAdvancedSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const renderSortLabel = (label: string, key: AdvancedSortKey) => (
    <button
      type="button"
      onClick={() => handleAdvancedSort(key)}
      className="inline-flex items-center gap-1 whitespace-nowrap hover:text-foreground"
    >
      <span>{label}</span>
      {advancedSort.key === key ? (
        advancedSort.direction === "asc" ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-40" />
      )}
    </button>
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Soft page entrance transition (keeps layout stable)
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setPageReady(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);


  // Ensure the search pill cutout is present before the first paint
  useLayoutEffect(() => {
    setSearchActive(true);
  }, [setSearchActive]);

  // Sync with URL mode on initial load (if URL says advanced, set it)
  useEffect(() => {
    setMounted(true);
  }, []);

  const effectiveAdvancedMode = mounted ? advancedMode : initialMode;
  const isMobile = useMobile();

  useEffect(() => {
    if (initialMode && !advancedMode) {
      setAdvancedMode(true);
    }
  }, [initialMode]);

  useEffect(() => {
    if (advancedMode || initialMode) {
      setIsLoading(false);
      return;
    }
    if (!query.trim()) {
      setResults(null);
      setIsLoading(false);
      return;
    }

    const fetchResults = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(query)}&limit=10`
        );

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.error ?? "Search is temporarily unavailable. Please try again shortly.");
        }

        const data = await response.json();
        console.log("[Search] people hits:", data?.people?.length ?? 0, data?.people?.slice(0,3));
        setResults(data);
        
        // Keep panel visible; loading state handles interim UI
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [query, advancedMode]);

  useEffect(() => {
    if (!advancedMode) {
      setAdvancedResults([]);
      setAdvancedTotal(0);
      setAdvancedError(null);
      setAdvancedLoading(false);
      setAdvancedPage(1);
      return;
    }

    const hasAdvancedInput =
      Boolean(query.trim()) ||
      Boolean(advancedFilters.sector?.length) ||
      Boolean(advancedFilters.county?.length) ||
      Boolean(advancedFilters.postcode?.trim()) ||
      Boolean(advancedFilters.division?.length) ||
      Boolean(advancedFilters.employeeBand?.length) ||
      Boolean(advancedFilters.boardStructure?.length) ||
      Boolean(advancedFilters.ageBand?.length) ||
      Boolean(advancedFilters.turnoverBand?.length) ||
      Boolean(advancedFilters.statusGroup) ||
      advancedFilters.hasContact !== null ||
      Boolean(advancedFilters.incorporatedToday);

    if (!hasAdvancedInput) {
      setAdvancedResults([]);
      setAdvancedTotal(0);
      setAdvancedError(null);
      setAdvancedLoading(false);
      setAdvancedPage(1);
      return;
    }

    const controller = new AbortController();

    const fetchAdvanced = async () => {
      setAdvancedError(null);
      try {
        const payload = buildAdvancedPayload();

        // Check cache BEFORE showing any loading state - cache hits are instant with no spinner
        const cacheKey = advancedFilters.incorporatedToday
          ? incorporatedTodayCacheKey(payload)
          : null;
        if (cacheKey) {
          const cached = readIncorporatedTodayCache(cacheKey);
          if (cached) {
            const resolved = cached as {
              results?: AdvancedSearchResult[];
              total?: number | string;
              count?: number | string;
            } | null;
            const resultRows = resolved?.results || [];
            const parsedTotal =
              typeof resolved?.total === "number"
                ? resolved.total
                : typeof resolved?.total === "string"
                ? Number(resolved.total)
                : null;
            const parsedCount =
              typeof resolved?.count === "number"
                ? resolved.count
                : typeof resolved?.count === "string"
                ? Number(resolved.count)
                : null;
            const inferredTotal =
              Number.isFinite(parsedTotal)
                ? (parsedTotal as number)
                : Number.isFinite(parsedCount)
                ? (parsedCount as number)
                : resultRows.length;
            setAdvancedResults(resultRows);
            setAdvancedTotal(inferredTotal);
            return;
          }
        }

        // No cache hit - show loading spinner and fetch from API
        setAdvancedLoading(true);

        setAdvancedDebug({
          payload,
          responseStatus: null,
          responseBody: null,
          durationMs: null,
          queryTimeMs: null,
        });

        const startTime = performance.now();
        const { body, status } = await runAdvancedSearch(payload, controller.signal);
        const endTime = performance.now();
        const queryTimeMs =
          typeof (body as { query_time_ms?: unknown } | null)?.query_time_ms === "number"
            ? ((body as { query_time_ms?: number }).query_time_ms ?? null)
            : null;
        setAdvancedDebug({
          payload,
          responseStatus: status,
          responseBody: body ?? null,
          durationMs: Math.max(0, Math.round(endTime - startTime)),
          queryTimeMs,
        });

        const resolved = body as {
          results?: AdvancedSearchResult[];
          total?: number | string;
          count?: number | string;
        } | null;
        const resultRows = resolved?.results || [];
        const parsedTotal =
          typeof resolved?.total === "number"
            ? resolved.total
            : typeof resolved?.total === "string"
            ? Number(resolved.total)
            : null;
        const parsedCount =
          typeof resolved?.count === "number"
            ? resolved.count
            : typeof resolved?.count === "string"
            ? Number(resolved.count)
            : null;
        const inferredTotal =
          Number.isFinite(parsedTotal)
            ? (parsedTotal as number)
            : Number.isFinite(parsedCount)
            ? (parsedCount as number)
            : resultRows.length;
        // Cache "Incorporated Today" results to localStorage for 5 minutes to avoid repeat round-trips
        if (cacheKey && resultRows.length > 0) {
          writeIncorporatedTodayCache(cacheKey, body);
        }
        setAdvancedResults(resultRows);
        setAdvancedTotal((prev) =>
          Number.isFinite(parsedTotal) || Number.isFinite(parsedCount)
            ? inferredTotal
            : advancedPage === 1
            ? inferredTotal
            : prev
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        setAdvancedError(err instanceof Error ? err.message : "Advanced search failed");
      } finally {
        setAdvancedLoading(false);
      }
    };

    fetchAdvanced();

    return () => controller.abort();
  }, [advancedMode, query, advancedFilters, advancedPage, advancedPageSize, advancedSort.key, advancedSort.direction]);

  const isAdvancedPending = advancedDebug?.responseStatus === null;
  const advancedStages = [
    "Checking filters…",
    "Connecting…",
    "Scanning the dataset…",
    "Ranking matches…",
    "Calculating metrics…",
    "Analyzing results…",
    "Validating totals…",
    "Preparing table…",
    "Formatting rows…",
    "Bear with us…",
    "Metaphorical bear…",
    "Almost there…",
  ];

  useEffect(() => {
    if (isAdvancedPending) {
      advancedLoadingStartRef.current = performance.now();
      if (advancedLoadingTimeoutRef.current) {
        window.clearTimeout(advancedLoadingTimeoutRef.current);
      }
      setAdvancedLoadingVisible(true);
      setAdvancedStageIndex(0);
      advancedStageTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      advancedStageTimeoutsRef.current = [];

      const stageDelays = advancedStages.map((_, idx) => (idx + 1) * 900).slice(0, -1);
      stageDelays.forEach((delay, index) => {
        const timeoutId = window.setTimeout(() => {
          setAdvancedStageIndex(Math.min(index + 1, advancedStages.length - 1));
        }, delay);
        advancedStageTimeoutsRef.current.push(timeoutId);
      });
      return;
    }

    const startedAt = advancedLoadingStartRef.current;
    const elapsed = startedAt ? performance.now() - startedAt : 0;
    const remaining = Math.max(0, 450 - elapsed);
    if (advancedLoadingTimeoutRef.current) {
      window.clearTimeout(advancedLoadingTimeoutRef.current);
    }
    advancedLoadingTimeoutRef.current = window.setTimeout(() => {
      setAdvancedLoadingVisible(false);
    }, remaining);
    advancedStageTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    advancedStageTimeoutsRef.current = [];
  }, [isAdvancedPending, advancedStages.length]);

  useEffect(() => {
    if (!advancedMode) return;
    setAdvancedPage(1);
  }, [advancedMode, query, advancedFilters]);

  useLayoutEffect(() => {
    if (!advancedMode) return;
    const updateWidth = () => {
      const scrollWidth = tableScrollRef.current?.scrollWidth ?? 0;
      const clientWidth = tableScrollRef.current?.clientWidth ?? 0;
      const width = Math.max(scrollWidth, clientWidth + 1);
      setBottomScrollWidth(width);
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined" || !tableScrollRef.current) {
      return;
    }
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(tableScrollRef.current);
    return () => observer.disconnect();
  }, [advancedMode, advancedResults, advancedPageSize]);


  const syncScrollFromTable = () => {
    if (!tableScrollRef.current || !bottomScrollRef.current) return;
    bottomScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
  };

  const syncScrollFromBottom = () => {
    if (!tableScrollRef.current || !bottomScrollRef.current) return;
    tableScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
  };

  const totalPages = Math.max(1, Math.ceil(advancedTotal / advancedPageSize));
  const canGoPrev = advancedPage > 1;
  const canGoNext = advancedPage < totalPages;
  const advancedStart = advancedTotal === 0 ? 0 : (advancedPage - 1) * advancedPageSize + 1;
  const advancedEnd =
    advancedTotal === 0
      ? 0
      : Math.min(advancedTotal, (advancedPage - 1) * advancedPageSize + advancedResults.length);

  const hasResults = results && (results.companies.length > 0 || groupedPeople.current.length > 0);
  const isEmpty = results && results.companies.length === 0 && groupedPeople.current.length === 0;
  const hasAdvancedResults = advancedResults.length > 0;
  const hasAdvancedInput =
    Boolean(query.trim()) ||
    Boolean(advancedFilters.sector?.length) ||
    Boolean(advancedFilters.county?.length) ||
    Boolean(advancedFilters.postcode?.trim()) ||
    Boolean(advancedFilters.division?.length) ||
    Boolean(advancedFilters.employeeBand?.length) ||
    Boolean(advancedFilters.ageBand?.length) ||
    Boolean(advancedFilters.turnoverBand?.length) ||
    Boolean(advancedFilters.statusGroup) ||
    advancedFilters.hasContact !== null;
  const showAdvancedEmpty =
    advancedMode &&
    !advancedLoading &&
    !advancedError &&
    hasAdvancedInput &&
    !hasAdvancedResults &&
    !isAdvancedPending &&
    !advancedLoadingVisible;

  const escapeCsvValue = (value: unknown) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const buildAdvancedCsv = (rows: AdvancedSearchResult[]) => {
    const headers = [
      "Company Name",
      "Company Number",
      "Status",
      "Is Open",
      "Industry",
      "Division",
      "County",
      "Post Town",
      "Postcode",
      "Incorporation Date",
      "Age Band",
      "Turnover (Year 1)",
      "Turnover Band",
      "Profit After Tax (Year 1)",
      "Total Assets (Year 1)",
      "Current Liabilities (Year 1)",
      "Cash (Year 1)",
      "Total Debt (Year 1)",
      "Employee Band",
      "Board Structure",
      "Director Avg Age",
      "Currency",
      "Website",
      "Domain",
      "Email",
      "Phone",
      "LinkedIn",
      "Twitter",
      "Facebook",
      "Instagram",
      "YouTube",
      "Companies House Link",
    ];

    const bodyRows = rows.map((row) => [
      row.Company_Name,
      row.Company_Number,
      row.Company_Status ?? "",
      row.Is_Open ?? "",
      row.Industry ?? "",
      row.Division ?? "",
      row.County ?? "",
      row.Post_Town ?? "",
      row.Postcode ?? "",
      row.Incorporation_Date ?? "",
      formatAgeBand(row.Age_Band),
      row.Turnover_Year1 ?? "",
      row.Turnover_Band ?? "",
      row.Profit_After_Tax_Year1 ?? "",
      row.Total_Assets_Year1 ?? "",
      row.Current_Liabilities_Year1 ?? "",
      row.Cash_Year1 ?? "",
      row.Total_Debt_Year1 ?? "",
      row.Employee_Band ?? "",
      row.Board_Structure ?? "",
      row.Director_Avg_Age ?? "",
      row.Currency ?? "",
      row.Website ?? "",
      row.Domain ?? "",
      row.Email ?? "",
      row.Phone ?? "",
      row.LinkedIn ?? "",
      row.Twitter ?? "",
      row.Facebook ?? "",
      row.Instagram ?? "",
      row.YouTube ?? "",
      row.Companies_House_Link ?? "",
    ]);

    return [headers, ...bodyRows]
      .map((line) => line.map(escapeCsvValue).join(","))
      .join("\n");
  };

  const downloadCsv = (rows: AdvancedSearchResult[], label: "page" | "all") => {
    const csv = buildAdvancedCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    link.href = url;
    link.download = `datadini-advanced-${label}-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyResults = async () => {
    try {
      if (advancedResults.length === 0) {
        setCopyStatus("error");
        window.setTimeout(() => setCopyStatus("idle"), 1600);
        return;
      }
      const csv = buildAdvancedCsv(advancedResults);
      await navigator.clipboard.writeText(csv);
      setCopyStatus("copied");
      window.setTimeout(() => setCopyStatus("idle"), 1600);
    } catch {
      setCopyStatus("error");
      window.setTimeout(() => setCopyStatus("idle"), 1600);
    }
  };

  const handleExportCurrentPage = () => {
    try {
      if (advancedResults.length === 0) {
        setExportStatus("error");
        window.setTimeout(() => setExportStatus("idle"), 1600);
        return;
      }
      downloadCsv(advancedResults, "page");
      setExportStatus("done");
      window.setTimeout(() => setExportStatus("idle"), 1600);
    } catch {
      setExportStatus("error");
      window.setTimeout(() => setExportStatus("idle"), 1600);
    }
  };

  const handleExportAllResults = async () => {
    const MAX_EXPORT_ROWS = isSubscribed ? 50000 : 100;
    if (advancedTotal === 0) {
      setExportStatus("error");
      setExportError("No results to export.");
      window.setTimeout(() => setExportStatus("idle"), 1600);
      return;
    }
    setIsExportingAll(true);
    setExportError(null);
    try {
      const payload = {
        ...buildAdvancedPayload({ limit: MAX_EXPORT_ROWS, offset: 0 }),
      };

      // 58s client-side timeout — matches server maxDuration
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 58_000);

      let body: unknown;
      try {
        const result = await runAdvancedSearch(payload, controller.signal);
        body = result.body;
      } finally {
        clearTimeout(timeoutId);
      }

      const resolved = body as { results?: AdvancedSearchResult[]; error?: string } | null;

      if (resolved?.error) {
        setExportStatus("error");
        setExportError(resolved.error);
        return;
      }

      const rows = resolved?.results || [];
      if (rows.length === 0) {
        setExportStatus("error");
        setExportError("The export returned no rows. Try adjusting your filters.");
        return;
      }
      downloadCsv(rows, "all");
      setExportStatus("done");
      window.setTimeout(() => setExportStatus("idle"), 1600);
    } catch (err: any) {
      setExportStatus("error");
      if (err?.name === "AbortError") {
        setExportError("Export timed out. Try narrowing your filters and exporting again.");
      } else {
        setExportError(err?.message || "Export failed. Please try again.");
      }
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleExportCurrentAndClose = () => {
    handleExportCurrentPage();
    setExportDialogOpen(false);
  };

  const handleExportAllAndClose = async () => {
    await handleExportAllResults();
    // Only close the dialog if the export succeeded
    if (exportStatus !== "error") {
      setExportDialogOpen(false);
    }
  };


  const formatCurrency = (value?: number | null) => {
    if (!value || Number.isNaN(value)) return "-";
    const absValue = Math.abs(value);
    const maximumFractionDigits = absValue >= 1_000_000 ? 1 : absValue >= 1_000 ? 1 : 0;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits,
    }).format(value);
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatLocation = (row: AdvancedSearchResult) => {
    const parts = [row.Post_Town, row.County, row.Postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const formatAddress = (row: AdvancedSearchResult) => {
    if (row.Full_Address) return row.Full_Address;
    const parts = [row.Post_Town, row.County, row.Postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const formatAgeBand = (value?: string | null) => {
    if (!value) return "-";
    const cleaned = value
      .trim()
      .replace(/\s*-\s*/g, "-")
      .replace(/\s+/g, " ")
      .replace(/\bLong standing\b/i, "Long-standing")
      .replace(/\bLongstanding\b/i, "Long-standing")
      .replace(/\bBrand new\b/i, "Brand New");
    const canonical = [
      "0-1 Years (Brand New)",
      "2-3 Years (Startup)",
      "4-5 Years (Growing)",
      "6-10 Years (Established)",
      "11-20 Years (Mature)",
      "21-50 Years (Long-standing)",
      "50+ Years (Historic)",
    ];
    const prefix = cleaned.split(" (")[0];
    const matched = canonical.find((band) => band.startsWith(prefix));
    if (matched) return matched;
    if (/^not reported$/i.test(cleaned) || /^unknown$/i.test(cleaned) || /^no accounts$/i.test(cleaned)) {
      return "-";
    }
    return cleaned;
  };

  const isClosedStatus = (status?: string | null, isOpen?: boolean | number | null) => {
    if (isOpen === true || isOpen === 1) return false;
    if (isOpen === false || isOpen === 0) return true;
    if (!status) return true;
    return CLOSED_STATUSES.includes(status);
  };

  const cacheSnapshot = (companyNumber: string, snapshot: Record<string, unknown>) => {
    try {
      sessionStorage.setItem(
        `companySnapshot:${companyNumber}`,
        JSON.stringify({ snapshot, savedAt: Date.now() })
      );
    } catch {
      // ignore
    }
  };

  const cacheAdvancedSnapshot = (row: AdvancedSearchResult) => {
    if (!row?.Company_Number) return;
    cacheSnapshot(row.Company_Number, { ...row });
  };

  const INCORPORATED_TODAY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  const incorporatedTodayCacheKey = (payload: Record<string, unknown>) => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const stablePayload = Object.fromEntries(
      Object.entries(payload)
        .filter(([k]) => k !== "offset") // offset varies by page - intentionally exclude
        .sort(([a], [b]) => a.localeCompare(b))
    );
    return `incorporatedTodaySearch:${dateStr}:${JSON.stringify(stablePayload)}`;
  };

  const readIncorporatedTodayCache = (key: string) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { body, savedAt } = JSON.parse(raw) as { body: unknown; savedAt: number };
      if (Date.now() - savedAt > INCORPORATED_TODAY_CACHE_TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return body;
    } catch {
      return null;
    }
  };

  const writeIncorporatedTodayCache = (key: string, body: unknown) => {
    try {
      // Prune any stale incorporated-today cache entries before writing
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith("incorporatedTodaySearch:") && k !== key) {
          localStorage.removeItem(k);
        }
      }
      localStorage.setItem(key, JSON.stringify({ body, savedAt: Date.now() }));
    } catch {
      // storage full or unavailable - silently ignore
    }
  };

  const cacheSimpleSnapshot = (company: Company) => {
    if (!company?.company_number) return;
    cacheSnapshot(company.company_number, {
      Company_Number: company.company_number,
      Company_Name: company.company_name,
      Company_Status: company.company_status ?? null,
      Company_Type: company.company_type ?? null,
      Incorporation_Date: company.incorporation_date ?? null,
      Postcode: company.registered_address_postcode ?? null,
      Post_Town: company.registered_address_town ?? null,
      Sector: company.sector ?? null,
    });
  };

  const getAdvancedCompanyUrl = (row: AdvancedSearchResult) => {
    const nameSlug = slugify(row.Company_Name || "company");
    const base = `/company/${row.Company_Number}-${nameSlug}`;
    return `${base}?mode=advanced`;
  };


  // Clip paths for cutout effect on inner panel (simple mode only)
  // Exact same values as profile page for consistent cutout position
  const innerCutoutClipPath = `polygon(
    0 12px, 3px 6px, 6px 3px, 12px 0,
    calc(100% - 12px) 0, calc(100% - 6px) 3px, calc(100% - 3px) 6px, 100% 12px,
    100% 100%,
    calc(50% + 418px) 100%, 
    calc(50% + 416px) calc(100% - 3px), 
    calc(50% + 412px) calc(100% - 8px),
    calc(50% + 406px) calc(100% - 15px), 
    calc(50% + 396px) calc(100% - 28px), 
    calc(50% + 378px) calc(100% - 48px),
    calc(50% + 353px) calc(100% - 68px), 
    calc(50% + 323px) calc(100% - 82px), 
    calc(50% + 288px) calc(100% - 88px),
    calc(50% - 288px) calc(100% - 88px), 
    calc(50% - 323px) calc(100% - 82px), 
    calc(50% - 353px) calc(100% - 68px),
    calc(50% - 378px) calc(100% - 48px), 
    calc(50% - 396px) calc(100% - 28px), 
    calc(50% - 406px) calc(100% - 15px),
    calc(50% - 412px) calc(100% - 8px), 
    calc(50% - 416px) calc(100% - 3px), 
    calc(50% - 418px) 100%,
    0 100%
  )`;
  
  const innerFullClipPath = `polygon(
    0 12px, 3px 6px, 6px 3px, 12px 0,
    calc(100% - 12px) 0, calc(100% - 6px) 3px, calc(100% - 3px) 6px, 100% 12px,
    100% 100%,
    calc(50% + 418px) 100%, 
    calc(50% + 416px) 100%, 
    calc(50% + 412px) 100%,
    calc(50% + 406px) 100%, 
    calc(50% + 396px) 100%, 
    calc(50% + 378px) 100%,
    calc(50% + 353px) 100%, 
    calc(50% + 323px) 100%, 
    calc(50% + 288px) 100%,
    calc(50% - 288px) 100%, 
    calc(50% - 323px) 100%, 
    calc(50% - 353px) 100%,
    calc(50% - 378px) 100%, 
    calc(50% - 396px) 100%, 
    calc(50% - 406px) 100%,
    calc(50% - 412px) 100%, 
    calc(50% - 416px) 100%, 
    calc(50% - 418px) 100%,
    0 100%
  )`;

  // ─── MOBILE VIEW (early return — desktop code below is untouched) ──────────
  if (mounted && isMobile) {
    const hasSimpleResults = results && (results.companies.length > 0 || groupedPeople.current.length > 0);
    const hasAdvancedResults = advancedResults.length > 0;
    const totalPages = Math.ceil(advancedTotal / advancedPageSize);
    const canGoPrev = advancedPage > 1;
    const canGoNext = advancedPage < totalPages;

    return (
      <div className="fixed inset-0 pt-[5vh] overflow-hidden app-gradient-bg">
        <div className="h-full w-full flex flex-col overflow-hidden bg-background/70 backdrop-blur-xl">

          {/* ── Simple mode results ── */}
          {!effectiveAdvancedMode && (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 pb-24 space-y-6">
              {(isLoading) && (
                <div className="flex items-center justify-center py-16">
                  <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                </div>
              )}
              {error && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
              )}
              {!isLoading && !error && !query.trim() && (
                <p className="text-sm text-muted-foreground text-center py-8">Enter a company name, director, or person above to search.</p>
              )}
              {!isLoading && !error && query.trim() && !hasSimpleResults && (
                <p className="text-sm text-muted-foreground text-center py-8">No results found for &ldquo;{query}&rdquo;</p>
              )}
              {results && results.companies.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-base font-semibold">Companies</h2>
                    <Badge variant="secondary" className="text-xs">{results.total_companies} found</Badge>
                  </div>
                  <div className="space-y-2">
                    {results.companies.map((company) => (
                      <Link
                        key={company.company_number}
                        href={getCompanyUrl(company, false)}
                        prefetch={false}
                        className="block"
                        onClick={() => cacheSimpleSnapshot(company)}
                      >
                        <Card className="transition-all hover:border-primary/30 active:scale-[0.99]">
                          <CardContent className="py-3 px-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{company.company_name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {company.company_number}
                                  {company.company_type && ` · ${company.company_type}`}
                                </p>
                                {company.registered_address_town && (
                                  <p className="text-xs text-muted-foreground">{company.registered_address_town}{company.registered_address_postcode && ` · ${company.registered_address_postcode}`}</p>
                                )}
                              </div>
                              {company.company_status && (
                                <Badge variant={company.company_status.toLowerCase() === "active" ? "default" : "secondary"} className="text-xs shrink-0">
                                  {company.company_status}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
              {results && results.companies.length > 0 && groupedPeople.current.length > 0 && <Separator />}
              {groupedPeople.current.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-base font-semibold">Directors &amp; Officers</h2>
                    <Badge variant="secondary" className="text-xs">{results?.total_people} found</Badge>
                  </div>
                  <div className="space-y-2">
                    {groupedPeople.current.map((person) => {
                      const personSlug = buildPersonSlug(person.person_name, person.dob_year, person.dob_month, person.nationality);
                      const activeCount = person.roles.filter(r => r.is_active).length;
                      return (
                        <Link key={`${person.person_name}__${person.dob_year}__${person.dob_month}__${person.nationality}`} href={`/people/${personSlug}`} prefetch={false} className="block">
                          <Card className="transition-all hover:border-primary/30 active:scale-[0.99]">
                            <CardContent className="py-3 px-4">
                              <p className="font-semibold text-sm">{person.person_name}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {activeCount} active role{activeCount !== 1 ? "s" : ""}
                                {person.nationality && ` · ${person.nationality}`}
                                {person.dob_year && ` · Born ${person.dob_year}`}
                              </p>
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ── Advanced mode results ── */}
          {effectiveAdvancedMode && (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
              {advancedLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                </div>
              )}
              {advancedError && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{advancedError}</div>
              )}
              {!advancedLoading && !advancedError && hasAdvancedResults && (
                <>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">{advancedTotal.toLocaleString()} companies</Badge>
                    {advancedTotal > advancedPageSize && <span className="text-xs text-muted-foreground">Page {advancedPage} of {totalPages}</span>}
                  </div>
                  <div className="space-y-2">
                    {advancedResults.map((co) => {
                      const nameSlug = co.Company_Name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").substring(0, 50);
                      const url = `/company/${co.Company_Number}-${nameSlug}`;
                      return (
                        <Link key={co.Company_Number} href={url} prefetch={false} className="block">
                          <Card className="transition-all hover:border-primary/30 active:scale-[0.99]">
                            <CardContent className="py-3 px-4">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-sm truncate">{co.Company_Name}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {co.Company_Number}
                                    {co.Industry && ` · ${co.Industry}`}
                                  </p>
                                  {(co.Full_Address || co.Post_Town) && (
                                    <p className="text-xs text-muted-foreground truncate">
                                      {co.Full_Address ?? `${co.Post_Town}${co.Postcode ? ` · ${co.Postcode}` : ""}`}
                                    </p>
                                  )}
                                  {co.Turnover_Year1 != null && (
                                    <p className="text-xs text-muted-foreground mt-0.5">Turnover: {typeof co.Turnover_Year1 === "number" ? co.Turnover_Year1.toLocaleString("en-GB", { style: "currency", currency: "GBP", notation: "compact" }) : co.Turnover_Year1}</p>
                                  )}
                                </div>
                                {co.Company_Status && (
                                  <Badge variant={(co.Is_Open === 1 || co.Is_Open === true) ? "default" : "secondary"} className="text-xs shrink-0">
                                    {co.Company_Status}
                                  </Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 pt-2">
                      <button onClick={() => setAdvancedPage(1)} disabled={!canGoPrev} className="text-xs text-muted-foreground disabled:opacity-30 border rounded px-2 py-1">First</button>
                      <button onClick={() => setAdvancedPage(p => Math.max(1, p - 1))} disabled={!canGoPrev} className="text-xs text-muted-foreground disabled:opacity-30 border rounded px-2 py-1">Prev</button>
                      <span className="text-xs">{advancedPage} / {totalPages}</span>
                      <button onClick={() => setAdvancedPage(p => Math.min(totalPages, p + 1))} disabled={!canGoNext} className="text-xs text-muted-foreground disabled:opacity-30 border rounded px-2 py-1">Next</button>
                      <button onClick={() => setAdvancedPage(totalPages)} disabled={!canGoNext} className="text-xs text-muted-foreground disabled:opacity-30 border rounded px-2 py-1">Last</button>
                    </div>
                  )}
                </>
              )}
              {!advancedLoading && !advancedError && !hasAdvancedResults && (
                <p className="text-sm text-muted-foreground text-center py-8">No results found. Adjust your filters and try again.</p>
              )}
            </div>
          )}

        </div>
      </div>
    );
  }
  // ─── END MOBILE VIEW ────────────────────────────────────────────────────────

  // Always render the panel structure, just control visibility
  return (
    <div className="fixed inset-0 pt-[5vh] overflow-hidden">
      {/* Gradient backdrop */}
      <div
        className="absolute inset-0 app-gradient-bg"
        style={{
          opacity: pageReady ? 1 : 0,
          transition: "opacity 0.9s ease",
        }}
      />
      
      {/* Content wrapper - always rendered, just opacity changes */}
      <div 
        className="relative h-full w-full flex flex-col items-center overflow-hidden"
        style={{
          paddingLeft: effectiveAdvancedMode ? '16px' : '32px',
          paddingRight: effectiveAdvancedMode ? '16px' : '32px',
          paddingTop: '16px',
          paddingBottom: '36px',
          opacity: pageReady ? 1 : 0,
          transform: pageReady ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}
      >
              
              {/* Main content panel */}
              <div 
                className="w-full flex-1 flex flex-col min-h-0 bg-background/70 backdrop-blur-xl shadow-xl border border-border/60 overflow-hidden"
                style={{ 
                  maxWidth: effectiveAdvancedMode ? '100%' : '64rem',
                  borderRadius: effectiveAdvancedMode ? '16px 16px 0 0' : '16px',
                  transition: 'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                
                {/* Inner solid panel - with cutout in simple mode when search is active */}
                <div 
                  className="flex-1 m-3 rounded-xl bg-card shadow-sm flex flex-col min-h-0 overflow-hidden"
                  style={{
                    clipPath: (searchActive && !effectiveAdvancedMode) ? innerCutoutClipPath : innerFullClipPath,
                    transition: 'clip-path 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
              
              {/* Header */}
              <div className="px-6 py-5 border-b border-border/60">
                <div className="flex items-center justify-between gap-4">
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                    {effectiveAdvancedMode ? 'Advanced Search Results' : 'Search Results'}
                  </h1>
                  <Button variant="ghost" size="sm" asChild>
                    <Link href="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                      Back to Home
                      <ArrowLeft className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                
              </div>
              
              {/* Content area */}
              <div className="flex-1 min-w-0 overflow-hidden p-6 flex flex-col">
                {/* Loading state */}
                {isLoading && (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 className="h-10 w-10 text-primary animate-spin" />
                    <p className="mt-4 text-muted-foreground">Searching...</p>
                  </div>
                )}

                {/* Error state */}
                {error && (
                  <Card className="border-destructive/50 bg-destructive/5">
                    <CardContent className="flex items-center gap-4 py-6">
                      <AlertCircle className="h-8 w-8 text-destructive flex-shrink-0" />
                      <div>
                        <p className="font-medium text-destructive">Search failed</p>
                        <p className="text-sm text-muted-foreground">{error}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Empty query state */}
                {!query && !isLoading && !effectiveAdvancedMode && (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <Search className="h-16 w-16 text-muted-foreground/40" />
                    <h2 className="mt-4 text-xl font-semibold">Enter a search term</h2>
                  </div>
                )}

                {/* Empty results state */}
                {isEmpty && !isLoading && !effectiveAdvancedMode && (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <Search className="h-16 w-16 text-muted-foreground/40" />
                    <h2 className="mt-4 text-xl font-semibold">No results found</h2>
                    <p className="mt-2 text-muted-foreground max-w-md">
                      No companies or people matched your search for "{query}". Try a different search term.
                    </p>
                  </div>
                )}

                  {/* Advanced Mode - Table View */}
                {effectiveAdvancedMode && (
                  <div className="flex flex-col gap-4 min-h-0 min-w-0 flex-1 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {/* Results info */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        {query && (
                          <span>
                            Showing results for: <span className="font-medium text-foreground">{query}</span>
                          </span>
                        )}
                        {advancedTotal > 0 && (
                          <span>
                            •{" "}
                            <span className="font-medium text-foreground">{advancedStart}</span>-
                            <span className="font-medium text-foreground">{advancedEnd}</span> of{" "}
                            <span className="font-medium text-foreground">{advancedTotal}</span> results
                          </span>
                        )}
                        {advancedTotal === 0 && (
                          <span>
                            • <span className="font-medium text-foreground">0</span> results
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <span>Rows per page:</span>
                          <select
                            className="border rounded px-2 py-1 text-xs bg-background"
                            value={advancedPageSize}
                            onChange={(event) => {
                              setAdvancedPageSize(Number(event.target.value));
                              setAdvancedPage(1);
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setAdvancedPage(1)}
                              disabled={!canGoPrev}
                            >
                              <ChevronsLeft className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setAdvancedPage((prev) => Math.max(1, prev - 1))}
                              disabled={!canGoPrev}
                            >
                              <ChevronLeft className="h-3.5 w-3.5" />
                            </Button>
                            <span className="px-2 text-xs text-muted-foreground">
                              {advancedPage} / {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setAdvancedPage((prev) => Math.min(totalPages, prev + 1))}
                              disabled={!canGoNext}
                            >
                              <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setAdvancedPage(totalPages)}
                              disabled={!canGoNext}
                            >
                              <ChevronsRight className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-8"
                            onClick={handleCopyResults}
                            disabled={advancedResults.length === 0}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1.5" />
                            {copyStatus === "copied"
                              ? "Copied"
                              : copyStatus === "error"
                              ? "Copy failed"
                              : "Copy"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-8"
                            onClick={() => setExportDialogOpen(true)}
                            disabled={advancedTotal === 0}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Export CSV {!isSubscribed && <span className="ml-1 text-muted-foreground/70">(100 row limit)</span>}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {advancedMode && (
                      <Dialog
                        open={exportDialogOpen}
                        onOpenChange={(open) => {
                          if (!isExportingAll) {
                            setExportDialogOpen(open);
                          }
                        }}
                      >
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Export results</DialogTitle>
                            <DialogDescription>
                              {isSubscribed
                                ? "Export the current page or all results. Exporting all results is capped at 50,000 rows."
                                : "Export the current page or all results. Free accounts are capped at 100 rows per export."}
                            </DialogDescription>
                            {!isSubscribed && (
                              <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">Need more?</span> Subscribe to export up to 50,000 rows and unlock full contact details.{" "}
                                <Link href="/account?tab=billing" className="text-primary underline hover:text-primary/80 font-medium">
                                  View plans
                                </Link>
                              </div>
                            )}
                            {isExportingAll && (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Exporting… this can take a moment.
                              </div>
                            )}
                            {exportStatus === "error" && exportError && (
                              <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
                                {exportError}
                              </div>
                            )}
                          </DialogHeader>
                          <DialogFooter className="gap-2 sm:gap-2">
                            <Button
                              variant="outline"
                              onClick={handleExportCurrentAndClose}
                              disabled={advancedResults.length === 0 || isExportingAll}
                            >
                              Export this page
                            </Button>
                            <Button
                              onClick={handleExportAllAndClose}
                              disabled={advancedTotal === 0 || isExportingAll}
                            >
                              {isExportingAll ? "Exporting..." : `Export all results${!isSubscribed ? " (up to 100)" : ""}`}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}

                    {showDebug && advancedDebug && (
                      <details className="rounded-md border border-dashed border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                        <summary className="cursor-pointer text-[11px] text-muted-foreground">
                          Advanced search debug
                        </summary>
                        <div className="mt-2 space-y-2">
                          <div>
                            <span className="font-medium text-foreground">Status:</span>{" "}
                            {advancedDebug.responseStatus ?? "pending"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Duration:</span>{" "}
                            {advancedDebug.durationMs !== null
                              ? `${advancedDebug.durationMs} ms (${(advancedDebug.durationMs / 1000).toFixed(3)} s)`
                              : "pending"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Query time:</span>{" "}
                            {advancedDebug.queryTimeMs !== null
                              ? `${advancedDebug.queryTimeMs} ms (${(advancedDebug.queryTimeMs / 1000).toFixed(3)} s)`
                              : "n/a"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Payload:</span>
                            <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-background px-2 py-1">
{JSON.stringify(advancedDebug.payload, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Response:</span>
                            <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-background px-2 py-1">
{JSON.stringify(advancedDebug.responseBody ?? null, null, 2)}
                            </pre>
                          </div>
                          {advancedFilters.ageBand?.length ? (
                            <div>
                              <span className="font-medium text-foreground">Age band filter:</span>{" "}
                              {advancedFilters.ageBand.join(", ")}
                            </div>
                          ) : null}
                          {advancedResults.length > 0 && (
                            <div>
                              <span className="font-medium text-foreground">Age band sample:</span>{" "}
                              {Array.from(
                                new Set(
                                  advancedResults
                                    .map((row) => row.Age_Band)
                                    .filter((value): value is string => Boolean(value))
                                )
                              )
                                .slice(0, 5)
                                .join(", ") || "none"}
                            </div>
                          )}
                        </div>
                      </details>
                    )}

                    {/* Data Table */}
                    <div className="rounded-md border flex-1 min-h-0 min-w-0 overflow-hidden relative">
                      <div
                        ref={tableScrollRef}
                        className="relative h-full min-w-0 overflow-y-auto overflow-x-hidden pb-4"
                        onScroll={syncScrollFromTable}
                      >
                        {mounted && (advancedLoadingVisible || isAdvancedPending) && (
                          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                            <div className="flex w-full max-w-md items-center justify-center gap-3 rounded-md border border-border/60 bg-muted/20 px-4 py-3 text-sm text-muted-foreground shadow-sm">
                              <DatadiniIcon className="size-[50px] text-primary animate-pulse" />
                              <div className="text-left">
                                <p className="text-xs font-medium text-foreground">
                                  {advancedStages[advancedStageIndex]}
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                  Pulling advanced results based on your filters
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {mounted &&
                          !advancedLoading &&
                          !isAdvancedPending &&
                          (advancedError || showAdvancedEmpty) && (
                          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                            <div
                              className={`flex w-full max-w-md items-center justify-center rounded-md border px-4 py-3 text-sm shadow-sm ${
                                advancedError
                                  ? "border-destructive/40 bg-destructive/5 text-destructive"
                                  : "border-border/60 bg-muted/20 text-muted-foreground"
                              }`}
                            >
                              {advancedError
                                ? advancedError
                                : query
                                ? `No advanced results found for "${query}".`
                                : "No advanced results found for the selected filters."}
                            </div>
                          </div>
                        )}
                        <table className="w-full caption-bottom text-sm">
                        <TableHeader className="sticky top-0 z-10 bg-background [&_th]:whitespace-nowrap">
                          <TableRow className="bg-muted/50 sticky top-0 z-10">
                            <TableHead className="min-w-[220px]">{renderSortLabel("Company Name", "Company_Name")}</TableHead>
                            <TableHead className="min-w-[120px]">{renderSortLabel("Company No.", "Company_Number")}</TableHead>
                            <TableHead className="min-w-[140px]">{renderSortLabel("Status", "Company_Status")}</TableHead>
                            <TableHead className="min-w-[200px]">{renderSortLabel("Industry", "Industry")}</TableHead>
                            <TableHead className="min-w-[200px]">{renderSortLabel("Division", "Division")}</TableHead>
                            <TableHead className="min-w-[200px]">{renderSortLabel("Address", "Post_Town")}</TableHead>
                            <TableHead className="min-w-[160px]">{renderSortLabel("County", "County")}</TableHead>
                            <TableHead className="min-w-[140px]">{renderSortLabel("Incorporated", "Incorporation_Date")}</TableHead>
                            <TableHead className="min-w-[160px]">{renderSortLabel("Age Band", "Age_Band")}</TableHead>
                            <TableHead className="min-w-[140px] text-right">{renderSortLabel("Turnover", "Turnover_Year1")}</TableHead>
                            <TableHead className="min-w-[140px] text-right">{renderSortLabel("Profit (Yr1)", "Profit_After_Tax_Year1")}</TableHead>
                            <TableHead className="min-w-[140px] text-right">{renderSortLabel("Assets (Yr1)", "Total_Assets_Year1")}</TableHead>
                            <TableHead className="min-w-[160px] text-right">{renderSortLabel("Liabilities (Yr1)", "Current_Liabilities_Year1")}</TableHead>
                            <TableHead className="min-w-[140px] text-right">{renderSortLabel("Cash (Yr1)", "Cash_Year1")}</TableHead>
                            <TableHead className="min-w-[140px] text-right">{renderSortLabel("Debt (Yr1)", "Total_Debt_Year1")}</TableHead>
                            <TableHead className="min-w-[150px]">{renderSortLabel("Employee Band", "Employee_Band")}</TableHead>
                            <TableHead className="min-w-[160px]">{renderSortLabel("Board Structure", "Board_Structure")}</TableHead>
                            <TableHead className="min-w-[140px] text-right">{renderSortLabel("Director Avg Age", "Director_Avg_Age")}</TableHead>
                            <TableHead className="min-w-[160px]">
                              <span className="flex items-center gap-1">
                                Website {!isSubscribed && <Link href="/account?tab=billing" className="text-[10px] text-primary/70 hover:text-primary underline font-normal">Subscribe to unlock</Link>}
                              </span>
                            </TableHead>
                            <TableHead className="min-w-[160px]">
                              <span className="flex items-center gap-1">
                                Email {!isSubscribed && <Link href="/account?tab=billing" className="text-[10px] text-primary/70 hover:text-primary underline font-normal">Subscribe to unlock</Link>}
                              </span>
                            </TableHead>
                            <TableHead className="min-w-[130px]">
                              <span className="flex items-center gap-1">
                                Phone {!isSubscribed && <Link href="/account?tab=billing" className="text-[10px] text-primary/70 hover:text-primary underline font-normal">Subscribe to unlock</Link>}
                              </span>
                            </TableHead>
                            <TableHead className="min-w-[160px]">
                              <span className="flex items-center gap-1">
                                Social {!isSubscribed && <Link href="/account?tab=billing" className="text-[10px] text-primary/70 hover:text-primary underline font-normal">Subscribe to unlock</Link>}
                              </span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {!advancedLoading && !advancedError && hasAdvancedResults && (
                            advancedResults.map((row) => (
                              <TableRow
                                key={row.Company_Number}
                                className="cursor-pointer hover:bg-muted/50 h-[56px]"
                                onMouseEnter={() => router.prefetch(getAdvancedCompanyUrl(row))}
                              >
                                <TableCell className="py-2 px-3 font-medium">
                                  <Link
                                    href={getAdvancedCompanyUrl(row)}
                                    className="hover:text-primary hover:underline flex items-start gap-1.5 min-w-0"
                                    onClick={() => cacheAdvancedSnapshot(row)}
                                  >
                                    <span style={clampStyle}>{row.Company_Name}</span>
                                    <ExternalLink className="h-3 w-3 opacity-50 flex-shrink-0" />
                                  </Link>
                                </TableCell>
                                <TableCell className="py-2 px-3 font-mono text-xs whitespace-nowrap">
                                  <Link
                                    href={getAdvancedCompanyUrl(row)}
                                    className="hover:text-primary hover:underline"
                                    onClick={() => cacheAdvancedSnapshot(row)}
                                  >
                                    {row.Company_Number}
                                  </Link>
                                </TableCell>
                                <TableCell className="py-2 px-3">
                                  <Badge
                                    className={`text-[10px] ${
                                      isClosedStatus(row.Company_Status, row.Is_Open)
                                        ? "bg-red-100 text-red-800"
                                        : "bg-emerald-100 text-emerald-800"
                                    }`}
                                  >
                                    {row.Company_Status || "-"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  <div style={clampStyle}>{row.Industry || "-"}</div>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  <div style={clampStyle}>{row.Division || "-"}</div>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  {formatAddress(row)}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  <div style={clampStyle}>{row.County || "-"}</div>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDate(row.Incorporation_Date)}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  <div style={clampStyle}>{formatAgeBand(row.Age_Band)}</div>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm whitespace-nowrap">
                                  {row.Turnover_Year1 ? formatCurrency(row.Turnover_Year1) : (row.Turnover_Band && !/^(no accounts|not reported|unknown)$/i.test(row.Turnover_Band.trim()) ? row.Turnover_Band : "-")}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm whitespace-nowrap">
                                  {row.Profit_After_Tax_Year1 ? formatCurrency(row.Profit_After_Tax_Year1) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm whitespace-nowrap">
                                  {row.Total_Assets_Year1 ? formatCurrency(row.Total_Assets_Year1) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm whitespace-nowrap">
                                  {row.Current_Liabilities_Year1 ? formatCurrency(row.Current_Liabilities_Year1) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm whitespace-nowrap">
                                  {row.Cash_Year1 ? formatCurrency(row.Cash_Year1) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm whitespace-nowrap">
                                  {row.Total_Debt_Year1 ? formatCurrency(row.Total_Debt_Year1) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  <div style={clampStyle}>{row.Employee_Band && !/^(no accounts|not reported|unknown)$/i.test(row.Employee_Band.trim()) ? row.Employee_Band : "-"}</div>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground leading-snug">
                                  <div style={clampStyle}>{row.Board_Structure || "-"}</div>
                                </TableCell>
                                <TableCell className="py-2 px-3 text-right text-sm text-muted-foreground whitespace-nowrap">
                                  {row.Director_Avg_Age ?? "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground whitespace-nowrap">
                                  {row.Website === "***" ? (
                                    <span className="text-muted-foreground/60 select-none">***</span>
                                  ) : row.Website ? (
                                    <a href={row.Website} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline truncate block max-w-[150px]" title={row.Website}>
                                      {row.Domain || row.Website}
                                    </a>
                                  ) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground whitespace-nowrap">
                                  {row.Email ? (
                                    row.Email.includes("*") ? (
                                      <span className="text-muted-foreground/60 select-none">{row.Email}</span>
                                    ) : (
                                      <a href={`mailto:${row.Email}`} className="hover:text-primary hover:underline truncate block max-w-[150px]" title={row.Email}>
                                        {row.Email}
                                      </a>
                                    )
                                  ) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground whitespace-nowrap">
                                  {row.Phone ? (
                                    row.Phone.includes("*") ? (
                                      <span className="text-muted-foreground/60 select-none">{row.Phone}</span>
                                    ) : (
                                      <a href={`tel:${row.Phone}`} className="hover:text-primary hover:underline">
                                        {row.Phone}
                                      </a>
                                    )
                                  ) : "-"}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-sm text-muted-foreground whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    {row.LinkedIn && !row.LinkedIn.includes("*") && (
                                      <a href={row.LinkedIn} target="_blank" rel="noopener noreferrer" title="LinkedIn" className="hover:text-primary">in</a>
                                    )}
                                    {row.Twitter && !row.Twitter.includes("*") && (
                                      <a href={row.Twitter} target="_blank" rel="noopener noreferrer" title="X / Twitter" className="hover:text-primary">𝕏</a>
                                    )}
                                    {row.Facebook && !row.Facebook.includes("*") && (
                                      <a href={row.Facebook} target="_blank" rel="noopener noreferrer" title="Facebook" className="hover:text-primary">fb</a>
                                    )}
                                    {row.Instagram && !row.Instagram.includes("*") && (
                                      <a href={row.Instagram} target="_blank" rel="noopener noreferrer" title="Instagram" className="hover:text-primary">ig</a>
                                    )}
                                    {row.YouTube && !row.YouTube.includes("*") && (
                                      <a href={row.YouTube} target="_blank" rel="noopener noreferrer" title="YouTube" className="hover:text-primary">yt</a>
                                    )}
                                    {(row.LinkedIn === "***" || row.Twitter === "***" || row.Facebook === "***" || row.Instagram === "***" || row.YouTube === "***") && (
                                      <span className="text-muted-foreground/60 select-none">***</span>
                                    )}
                                    {!row.LinkedIn && !row.Twitter && !row.Facebook && !row.Instagram && !row.YouTube && "-"}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                        </table>
                      </div>
                      <div className="sticky bottom-0 left-0 w-full bg-background/95 backdrop-blur-sm border-t border-border/60">
                        <div
                          ref={bottomScrollRef}
                          className="h-3.5 overflow-x-scroll overflow-y-hidden"
                          onScroll={syncScrollFromBottom}
                        >
                          <div style={{ width: bottomScrollWidth, height: 1 }} />
                        </div>
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        Page {advancedPage} of {totalPages}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setAdvancedPage(1)}
                          disabled={!canGoPrev}
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setAdvancedPage((prev) => Math.max(1, prev - 1))}
                          disabled={!canGoPrev}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 px-3 font-medium" disabled>
                          {advancedPage}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setAdvancedPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={!canGoNext}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setAdvancedPage(totalPages)}
                          disabled={!canGoNext}
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Simple Mode - Card Results */}
                {!effectiveAdvancedMode && hasResults && !isLoading && (
                  <div className="flex flex-col min-h-0 min-w-0 flex-1 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        {query && (
                          <span>
                            Showing results for: <span className="font-medium text-foreground">{query}</span>
                          </span>
                        )}
                        <span>
                          • <span className="font-medium text-foreground">{results.total_companies}</span> companies
                        </span>
                        <span>
                          •{" "}
                          <span className="font-medium text-foreground">{results.total_people}</span>{" "}
                          {results.total_people === 1 ? "person" : "people"}
                        </span>
                      </div>
                      <div />
                    </div>
                    <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden space-y-8 pr-4">
                      {/* Companies Section */}
                      {results.companies.length > 0 && (
                        <section className="min-w-0">
                          <div className="flex items-center gap-3 mb-4">
                            <Building2 className="h-5 w-5 text-primary" />
                            <h2 className="text-xl font-semibold">Companies</h2>
                            <Badge variant="secondary" className="ml-auto">
                              {results.total_companies} found
                            </Badge>
                          </div>
                          
                          <div className="grid w-full gap-3 min-w-0">
                            {results.companies.map((company) => (
                              <Link
                                key={company.company_number}
                                href={getCompanyUrl(company, advancedMode)}
                                prefetch={false}
                                className="block group min-w-0"
                                onClick={() => cacheSimpleSnapshot(company)}
                                onMouseEnter={() => router.prefetch(getCompanyUrl(company, advancedMode))}
                              >
                              <Card className="w-full min-w-0 transition-all hover:shadow-md hover:border-primary/30 group-focus-visible:ring-2 group-focus-visible:ring-primary">
                                <CardContent className="w-full min-w-0 py-4">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                                            {company.company_name}
                                          </h3>
                                          <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">
                                          {company.company_number}
                                          {company.company_type && ` · ${company.company_type}`}
                                        </p>
                                        {company.full_address && (
                                          <p className="text-sm text-muted-foreground mt-1 truncate">
                                            {company.full_address}
                                          </p>
                                        )}
                                      </div>
                                      {company.company_status && (
                                        <Badge
                                          variant={
                                            company.company_status.toLowerCase() === "active"
                                              ? "default"
                                              : "secondary"
                                          }
                                          className="flex-shrink-0"
                                        >
                                          {company.company_status}
                                        </Badge>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              </Link>
                            ))}
                          </div>
                        </section>
                      )}

                      {/* Separator between sections */}
                      {results.companies.length > 0 && groupedPeople.current.length > 0 && (
                        <Separator />
                      )}

                      {/* People Section */}
                    {groupedPeople.current.length > 0 && (
                      <section className="min-w-0">
                          <div className="flex items-center gap-3 mb-4">
                            <User className="h-5 w-5 text-primary" />
                            <h2 className="text-xl font-semibold">Directors and Officers</h2>
                            <Badge variant="secondary" className="ml-auto">
                              {results.total_people} found
                            </Badge>
                          </div>
                          
                        <div className="grid w-full gap-3 min-w-0">
                            {groupedPeople.current.map((person) => {
                              const activeRoles = person.roles.filter(r => r.is_active);
                              const activeOfficer = activeRoles.filter(r => r.role_type === "OFFICER");
                              const activePsc = activeRoles.filter(r => r.role_type === "PSC");
                              const activeCompanyNames = activeRoles
                                .map(r => r.company_name)
                                .filter((v, i, a) => a.indexOf(v) === i); // dedupe

                              // Build slug: name + year + month + nationality
                              const personSlug = buildPersonSlug(person.person_name, person.dob_year, person.dob_month, person.nationality);

                              // Role type label for the card
                              const roleTypes = Array.from(
                                new Set(person.roles.map(r =>
                                  r.appointment_type === "director" || r.appointment_type === "Director"
                                    ? "Director"
                                    : r.role_type === "PSC"
                                    ? "Significant Control"
                                    : r.appointment_type || "Officer"
                                ))
                              ).slice(0, 2);

                              return (
                                <Link
                                  key={`${person.person_name}__${person.dob_year}__${person.dob_month}__${person.nationality}`}
                                  href={`/people/${personSlug}`}
                                  prefetch={false}
                                  className="block group min-w-0"
                                  onMouseEnter={() => router.prefetch(`/people/${personSlug}`)}
                                >
                                  <Card className="w-full min-w-0 transition-all hover:shadow-md hover:border-primary/30 group-focus-visible:ring-2 group-focus-visible:ring-primary">
                                    <CardContent className="w-full min-w-0 py-4">
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                          {/* Name */}
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                              {person.person_name}
                                            </h3>
                                            <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                          </div>

                                          {/* Role type + nationality */}
                                          {(roleTypes.length > 0 || person.nationality) && (
                                            <p className="text-sm text-muted-foreground mt-0.5">
                                              {[roleTypes.join(" · "), person.nationality].filter(Boolean).join(" · ")}
                                              {person.dob_year && (
                                                <span className="ml-2 text-xs">
                                                  · Born {person.dob_month ? `${String(person.dob_month).padStart(2,"0")}/` : ""}{person.dob_year}
                                                </span>
                                              )}
                                            </p>
                                          )}

                                          {/* Active at companies */}
                                          {activeCompanyNames.length > 0 && (
                                            <p className="text-xs text-muted-foreground mt-1.5">
                                              <span className="font-medium">Active at: </span>
                                              {activeCompanyNames.slice(0, 3).join(", ")}
                                              {activeCompanyNames.length > 3 && (
                                                <span className="text-muted-foreground/70"> +{activeCompanyNames.length - 3} more</span>
                                              )}
                                            </p>
                                          )}

                                          {/* Role counts */}
                                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                            {activeOfficer.length > 0 && (
                                              <span>{activeOfficer.length} officer role{activeOfficer.length !== 1 ? "s" : ""}</span>
                                            )}
                                            {activePsc.length > 0 && (
                                              <span>{activePsc.length} PSC role{activePsc.length !== 1 ? "s" : ""}</span>
                                            )}
                                            {activeRoles.length === 0 && person.roles.length > 0 && (
                                              <span className="text-muted-foreground/60">{person.roles.length} past role{person.roles.length !== 1 ? "s" : ""}</span>
                                            )}
                                          </div>
                                        </div>

                                        {/* Active badge */}
                                        {activeRoles.length > 0 && (
                                          <Badge variant="default" className="flex-shrink-0">
                                            Active
                                          </Badge>
                                        )}
                                      </div>
                                    </CardContent>
                                  </Card>
                                </Link>
                              );
                            })}
                          </div>
                        </section>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}
